import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, ShieldCheck, Eye } from 'lucide-react';
import toast from '../../../utils/toast';
import { formatDateTime } from '../../../utils/dateUtils';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import CountBadge from '../../../components/UI/CountBadge';

/**
 * Admin access log review (inventory-agnostic — serves both the Big
 * Five and DISC tabs).
 *
 * Returns the most recent individual-result access events with viewer,
 * viewed employee, justification code, free-text justification, and IP.
 *
 * Free-text filter applies client-side across viewer name, viewed name,
 * justification code, and free text.
 *
 * Props
 * -----
 * - `fetchAccessLog`  API method returning `{ entries: [...] }`
 *                     (e.g. `adminGetBigFiveAccessLog`).
 */

const JUSTIFICATION_LABELS = {
  talent_development: 'Talent development',
  coaching: 'Coaching',
  role_fit: 'Role-fit discussion',
  succession_planning: 'Succession planning',
  performance_review_support: 'Performance review support',
  employee_requested_discussion: 'Employee-requested discussion',
  other: 'Other',
};

const JUSTIFICATION_TONE = {
  talent_development: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  coaching: 'bg-blue-50 text-blue-700 border-blue-200',
  role_fit: 'bg-violet-50 text-violet-700 border-violet-200',
  succession_planning: 'bg-amber-50 text-amber-700 border-amber-200',
  performance_review_support: 'bg-orange-50 text-orange-700 border-orange-200',
  employee_requested_discussion: 'bg-teal-50 text-teal-700 border-teal-200',
  other: 'bg-gray-50 text-gray-700 border-gray-200',
};


const AssessmentAccessLog = ({ fetchAccessLog }) => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchAccessLog({ limit: 200 });
      setEntries(data?.entries || []);
    } catch (err) {
      toast.error('Could not load the access log.');
    } finally {
      setLoading(false);
    }
  }, [fetchAccessLog]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const fields = [
        e.viewer?.display_name, e.viewer?.staff_id,
        e.viewed_employee?.display_name, e.viewed_employee?.staff_id,
        e.justification_code, e.justification_text,
      ];
      return fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [entries, search]);

  return (
    <div className="space-y-4">
      {/* Toolbar — search on the left (admin-list convention), event count on the right */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <DebouncedSearchInput
          value={search}
          onSearch={setSearch}
          placeholder="Search viewer, viewed, reason"
          theme="admin"
          className="w-full sm:flex-1 sm:max-w-xl"
        />
        <div
          className="flex items-center gap-2 text-sm text-gray-600 self-end sm:self-auto flex-shrink-0"
          title={`${entries.length} recent access event${entries.length === 1 ? '' : 's'}`}
        >
          <ShieldCheck className="w-4 h-4 text-red-600" aria-hidden="true" />
          <span>Recent events</span>
          <CountBadge count={entries.length} theme="admin" size="md" />
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center">
          <Eye className="w-10 h-10 text-gray-300 mx-auto" />
          <h3 className="mt-3 text-base font-semibold text-gray-900">
            {entries.length === 0 ? 'No access events yet' : 'No matches'}
          </h3>
          <p className="mt-1 text-sm text-gray-600 max-w-md mx-auto">
            {entries.length === 0
              ? 'Individual-result accesses will appear here as soon as HR starts using the directory.'
              : 'Adjust your search or clear the filter to see all events.'}
          </p>
        </div>
      ) : (
        <>
          <div className="md:hidden space-y-3">
            {filtered.map((entry) => {
              const tone = JUSTIFICATION_TONE[entry.justification_code]
                || JUSTIFICATION_TONE.other;
              const viewer = entry.viewer?.display_name || entry.viewer?.staff_id || '—';
              const viewed = entry.viewed_employee?.display_name
                || entry.viewed_employee?.staff_id
                || '—';
              return (
                <article
                  key={entry.id}
                  className="rounded-xl border border-gray-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                        Viewer
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-gray-900 truncate">
                        {viewer}
                      </p>
                      {entry.viewer?.staff_id && (
                        <p className="text-xs text-gray-500">{entry.viewer.staff_id}</p>
                      )}
                    </div>
                    <time className="text-xs text-gray-500 text-right flex-shrink-0">
                      {formatDateTime(entry.accessed_at)}
                    </time>
                  </div>

                  <div className="mt-3 border-t border-gray-100 pt-3">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                      Viewed employee
                    </p>
                    <p className="mt-0.5 text-sm font-medium text-gray-900">{viewed}</p>
                    {entry.viewed_employee?.staff_id && (
                      <p className="text-xs text-gray-500">{entry.viewed_employee.staff_id}</p>
                    )}
                  </div>

                  <div className="mt-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${tone}`}>
                      {JUSTIFICATION_LABELS[entry.justification_code]
                        || entry.justification_code}
                    </span>
                    {entry.justification_text && (
                      <p className="mt-2 text-xs text-gray-600 whitespace-pre-line">
                        {entry.justification_text}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Viewer</th>
                <th className="px-4 py-3 font-semibold">Viewed employee</th>
                <th className="px-4 py-3 font-semibold">Reason</th>
                <th className="px-4 py-3 font-semibold hidden lg:table-cell">IP</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((e) => {
                const tone = JUSTIFICATION_TONE[e.justification_code] || JUSTIFICATION_TONE.other;
                return (
                  <tr key={e.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {formatDateTime(e.accessed_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {e.viewer?.display_name || e.viewer?.staff_id || '—'}
                      </div>
                      {e.viewer?.staff_id && (
                        <div className="text-xs text-gray-500">{e.viewer.staff_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {e.viewed_employee?.display_name || e.viewed_employee?.staff_id || '—'}
                      </div>
                      {e.viewed_employee?.staff_id && (
                        <div className="text-xs text-gray-500">{e.viewed_employee.staff_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${tone}`}>
                        {JUSTIFICATION_LABELS[e.justification_code] || e.justification_code}
                      </span>
                      {e.justification_text && (
                        <div className="mt-1 text-xs text-gray-600 max-w-md whitespace-pre-line">
                          {e.justification_text}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-500 font-mono">
                      {e.ip_address || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentAccessLog;
